<template>
  <div class="accept-invite-page">
    <!-- Header -->
    <div class="page-header accept-invite-hide-header">
      <div class="header-content">
        <div class="logo-section">
          <img :src="logoImage" :alt="t('invite.logoAlt')" class="logo" />
          <h1 class="app-title">{{ t('invite.appTitle') }}</h1>
        </div>
      </div>
    </div>
 

    <!-- Conteúdo principal -->
    <div class="page-content">
      <div class="invite-bg-decor" aria-hidden="true">
        <span class="invite-orb invite-orb--1"></span>
        <span class="invite-orb invite-orb--2"></span>
        <span class="invite-orb invite-orb--3"></span>
      </div>

      <div class="invite-logo-section">
        <img :src="logoImage" :alt="t('invite.logoAlt')" class="invite-logo" />
      </div>

      <div class="invite-container">
        <!-- Loading state -->
        <div v-if="loading" class="invite-card invite-card--loading">
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ t('invite.verifying') }}</p>
          </div>
        </div>

        <!-- Convite válido -->
        <div v-else-if="inviteValid" class="invite-card">
          <div class="invite-card-header">
            <p class="invite-eyebrow">{{ t('invite.eyebrow') }}</p>
            <h2 class="invite-title">{{ t('invite.title') }}</h2>
            <p class="invite-header-sub">{{ t('invite.subtitle') }}</p>
          </div>

          <div class="invite-card-body">
            <div class="workspace-spotlight">
              <div
                class="workspace-spotlight-avatar"
                :style="workspaceAvatarStyle"
                :aria-label="workspaceInfo?.nome ? t('invite.workspaceLogoAria', { name: workspaceInfo.nome }) : t('invite.workspaceLogoAriaGeneric')"
              >
                <img
                  v-if="workspaceLogoUrl && !workspaceLogoFailed"
                  :src="workspaceLogoUrl"
                  :alt="workspaceInfo?.nome || 'Workspace'"
                  class="workspace-spotlight-logo"
                  @error="workspaceLogoFailed = true"
                />
                <span v-else class="workspace-spotlight-initial">{{ workspaceInitial }}</span>
              </div>
              <div class="workspace-spotlight-copy">
                <span class="workspace-spotlight-label">{{ t('invite.workspaceLabel') }}</span>
                <h3 class="workspace-spotlight-name">{{ workspaceInfo?.nome }}</h3>
                <div
                  class="workspace-badge"
                  :class="`type-${(workspaceInfo?.tipo || 'TAREFAS').toLowerCase()}`"
                >
                  <div class="badge-icon">
                    <svg v-if="workspaceInfo?.tipo === 'CRM'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                    </svg>
                  </div>
                  <span class="badge-text">{{ workspaceInfo?.tipo || 'TAREFAS' }}</span>
                </div>
              </div>
            </div>

            <ul class="invite-perks" :aria-label="t('invite.perksAria')">
              <li>
                <span class="invite-perk-icon">✓</span>
                {{ t('invite.perkImmediate') }}
              </li>
              <li>
                <span class="invite-perk-icon">✓</span>
                {{ t('invite.perkIntegrated') }}
              </li>
              <li>
                <span class="invite-perk-icon">✓</span>
                {{ t('invite.perkSecure') }}
              </li>
            </ul>

          <!-- Usuário logado -->
          <div v-if="isAuthenticated" class="user-logged">
            <div class="user-info">
              <div class="user-avatar">
                <img 
                  v-if="userInfo?.fotoPerfil" 
                  :src="userInfo.fotoPerfil" 
                  :alt="userInfo.nome"
                  class="avatar-image"
                />
                <div v-else class="avatar-placeholder">
                  {{ userInfo?.nome?.charAt(0)?.toUpperCase() }}
                </div>
              </div>
              <div class="user-details">
                <h3 class="user-name">{{ userInfo?.nome }}</h3>
                <p class="user-email">{{ userInfo?.email }}</p>
              </div>
            </div>
            
            <div class="action-buttons">
              <button type="button" class="btn-primary btn-primary--glow" :disabled="accepting" @click="aceitarConvite">
                <svg v-if="!accepting" class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                <div v-else class="loading-spinner-small" aria-hidden="true"></div>
                {{ accepting ? t('invite.accepting') : t('invite.accept') }}
              </button>
              <button type="button" class="btn-secondary btn-secondary--ghost" @click="recusarConvite">
                <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
                {{ t('invite.decline') }}
              </button>
            </div>
          </div>

          <!-- Usuário não logado -->
          <div v-else class="user-not-logged">
            <div class="auth-callout">
              <p class="login-required">
                {{ t('invite.loginRequired') }}
              </p>
            </div>

            <div class="auth-buttons">
              <button type="button" class="btn-primary btn-primary--glow" @click="irParaLogin">
                <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/>
                </svg>
                {{ t('invite.login') }}
              </button>
              <button type="button" class="btn-secondary" @click="irParaCadastro">
                <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                </svg>
                {{ t('invite.registerFree') }}
              </button>
            </div>
          </div>
          </div>
        </div>

        <!-- Convite inválido -->
        <div v-else class="invite-card invite-card--error">
          <div class="invite-card-header invite-card-header--error">
            <div class="error-icon" aria-hidden="true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
            <h2 class="error-title">{{ t('invite.invalidTitle') }}</h2>
            <p class="error-description">
              {{ t('invite.invalidDescription') }}
            </p>
          </div>
          <div class="invite-card-body">
            <div class="error-actions">
              <button type="button" class="btn-primary btn-primary--glow" @click="irParaLogin">
                <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/>
                </svg>
                {{ t('invite.goToLogin') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useGlobalWorkspaces } from '@/composables/useGlobalWorkspaces'
import api from '@/services/api'
import { resolveMediaUrl } from '@/config/app'
import logoLight from '@/assets/logo_b.png'
import logoDark from '@/assets/logo_w.png'
import { trackMeta } from '@/utils/metaPixel'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const themeStore = useThemeStore()

// Usar workspaces globais para atualizar a sidebar
const { reloadWorkspaces } = useGlobalWorkspaces()

// Dados reativos
const loading = ref(true)
const inviteValid = ref(false)
const accepting = ref(false)
const workspaceInfo = ref(null)
const userInfo = ref(null)
const inviteToken = ref('')

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated)
const isLightTheme = computed(() => themeStore.isLight)
const logoImage = computed(() => {
  return isLightTheme.value ? logoLight : logoDark
})

const workspaceLogoFailed = ref(false)

const workspaceInitial = computed(() => {
  const nome = workspaceInfo.value?.nome?.trim()
  return nome ? nome.charAt(0).toUpperCase() : 'W'
})

const workspaceLogoUrl = computed(() => {
  const foto = workspaceInfo.value?.fotoWorkspace
  if (!foto || !String(foto).trim()) return ''
  return resolveMediaUrl(foto)
})

const workspaceAvatarStyle = computed(() => {
  if (workspaceLogoUrl.value && !workspaceLogoFailed.value) {
    return { background: 'var(--bg-primary)' }
  }
  const cor = workspaceInfo.value?.corWorkspace || '#6366f1'
  return {
    background: `linear-gradient(135deg, ${cor}, #8b5cf6)`
  }
})

watch(workspaceInfo, () => {
  workspaceLogoFailed.value = false
})

// Métodos
const verificarConvite = async () => {
  try {
    loading.value = true
    inviteToken.value = route.params.token
    
    console.log('Verificando convite com token:', inviteToken.value)
    
    // Verificar se o convite é válido
    const response = await api.get(`/workspace-acesso/convite/${inviteToken.value}/verificar`)
    
    console.log('Resposta da verificação:', response.data)
    
      if (response.data.valido) {
      inviteValid.value = true
      workspaceInfo.value = {
        id: response.data.workspaceId,
        nome: response.data.workspaceNome,
        tipo: response.data.workspaceTipo,
        fotoWorkspace: response.data.fotoWorkspace,
        corWorkspace: response.data.corWorkspace
      }
      
      console.log('Workspace info:', workspaceInfo.value)
      
      // Se usuário está logado, carregar informações do usuário
      if (isAuthenticated.value) {
        userInfo.value = authStore.user
        console.log('Usuário logado:', userInfo.value)
      } else {
        console.log('Usuário não logado — exibindo opções de login/cadastro')
      }
    } else {
      inviteValid.value = false
      console.log('Convite inválido')
    }
    
  } catch (error) {
    console.error('Erro ao verificar convite:', error)
    console.error('Status:', error.response?.status)
    console.error('Data:', error.response?.data)
    inviteValid.value = false
  } finally {
    loading.value = false
  }
}

const aceitarConvite = async () => {
  try {
    accepting.value = true
    
    console.log('🔍 Iniciando aceite de convite...')
    console.log('🔑 Token do convite:', inviteToken.value)
    console.log('👤 Usuário logado:', authStore.user)
    
    // Verificar se usuário está autenticado
    const token = localStorage.getItem('token')
    if (!token) {
      console.error('❌ Usuário não autenticado')
      alert(t('invite.mustLoginAccept'))
      return
    }
    
    console.log('🔑 Token de autenticação encontrado')
    
    const response = await api.post(`/workspace-acesso/convite/${inviteToken.value}/aceitar`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    
    console.log('✅ Resposta do aceitar convite:', response.data)
    
    if (response.data.sucesso === true) {
      console.log('🎉 Convite aceito com sucesso!')
      
      // Recarregar workspaces para atualizar a sidebar
      try {
        console.log('🔄 Recarregando workspaces...')
        await reloadWorkspaces()
        console.log('✅ Workspaces recarregados com sucesso!')
        
        // Pequeno delay para garantir que a sidebar seja atualizada
        await new Promise(resolve => setTimeout(resolve, 500))
      } catch (error) {
        console.warn('⚠️ Erro ao recarregar workspaces:', error)
        // Não falhar o processo se não conseguir recarregar
      }
      
      // Redirecionar para o dashboard para ver o workspace na listagem
      router.push('/dashboard')
    } else {
      console.error('❌ Erro ao aceitar convite:', response.data.mensagem)
      alert('Erro ao aceitar convite: ' + response.data.mensagem)
    }
    
  } catch (error) {
    console.error('❌ Erro ao aceitar convite:', error)
    console.error('📊 Status:', error.response?.status)
    console.error('📄 Data:', error.response?.data)
    
    if (error.response?.status === 401) {
      alert(t('invite.sessionExpired'))
      router.push('/login')
    } else if (error.response?.status === 403) {
      alert(t('invite.noPermissionAccept'))
    } else {
      alert('Erro ao aceitar convite. Tente novamente.')
    }
  } finally {
    accepting.value = false
  }
}

const recusarConvite = async () => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      alert(t('invite.mustLoginDecline'))
      return
    }
    
    await api.post(`/workspace-acesso/convite/${inviteToken.value}/recusar`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    router.push('/dashboard')
  } catch (error) {
    console.error('Erro ao recusar convite:', error)
    alert('Erro ao recusar convite. Tente novamente.')
  }
}

const irParaLogin = () => {
  // Salvar token do convite no localStorage para usar após login
  localStorage.setItem('inviteToken', inviteToken.value)
  router.push('/login')
}

const irParaCadastro = () => {
  trackMeta('InitiateCheckout')
  // Salvar token do convite no localStorage para usar após cadastro
  localStorage.setItem('inviteToken', inviteToken.value)
  router.push('/register')
}

// Lifecycle
onMounted(() => {
  verificarConvite()
})
</script>

<style scoped>
.accept-invite-page {
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
  color: var(--text-primary);
  background-color: var(--bg-primary);
  background-image:
    radial-gradient(ellipse 90% 60% at 50% -15%, rgba(99, 102, 241, 0.22), transparent 55%),
    radial-gradient(ellipse 55% 45% at 100% 40%, rgba(139, 92, 246, 0.14), transparent 50%),
    radial-gradient(ellipse 45% 35% at 0% 85%, rgba(16, 185, 129, 0.1), transparent 45%);
}

.page-header {
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  border-bottom: 1px solid var(--border-primary);
  padding: 20px 0;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  height: 32px;
  width: auto;
}

.app-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.page-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 100dvh;
  padding: 32px 24px 48px;
  gap: 20px;
  box-sizing: border-box;
  isolation: isolate;
}

.invite-bg-decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.invite-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
  animation: invite-float 12s ease-in-out infinite;
}

.invite-orb--1 {
  width: 280px;
  height: 280px;
  top: 8%;
  left: 10%;
  background: rgba(99, 102, 241, 0.35);
}

.invite-orb--2 {
  width: 220px;
  height: 220px;
  top: 55%;
  right: 8%;
  background: rgba(139, 92, 246, 0.28);
  animation-delay: -4s;
}

.invite-orb--3 {
  width: 180px;
  height: 180px;
  bottom: 12%;
  left: 25%;
  background: rgba(16, 185, 129, 0.2);
  animation-delay: -8s;
}

@keyframes invite-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(12px, -18px) scale(1.05); }
}

@media (min-height: 820px) {
  .page-content {
    justify-content: center;
    padding: 40px 24px;
    gap: 32px;
  }
}

.invite-logo-section {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
}

.invite-logo {
  height: 440px;
  max-height: min(440px, 56vh);
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 12px 32px rgba(99, 102, 241, 0.3));
}

.invite-brand {
  font-size: 48px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -1px;
}

.invite-container {
  position: relative;
  z-index: 1;
  max-width: 520px;
  width: 100%;
}

.invite-card {
  overflow: hidden;
  border-radius: 4px;
  background: var(--bg-secondary);
  border: 1px solid rgba(99, 102, 241, 0.22);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 4px 6px rgba(0, 0, 0, 0.04),
    0 24px 48px -12px rgba(0, 0, 0, 0.28),
    0 0 100px rgba(99, 102, 241, 0.12);
  animation: invite-card-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes invite-card-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.invite-card--loading {
  padding: 48px 32px;
  text-align: center;
}

.invite-card-header {
  padding: 32px 28px 24px;
  text-align: center;
  background: linear-gradient(145deg, #6366f1 0%, #7c3aed 48%, #8b5cf6 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.invite-card-header::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 0%, rgba(255, 255, 255, 0.2), transparent 45%),
    radial-gradient(circle at 80% 100%, rgba(0, 0, 0, 0.15), transparent 40%);
  pointer-events: none;
}

.invite-card-header--error {
  background: linear-gradient(145deg, #dc2626 0%, #b91c1c 100%);
}

.invite-eyebrow {
  position: relative;
  z-index: 1;
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.9;
}

.invite-title {
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.invite-header-sub {
  position: relative;
  z-index: 1;
  margin: 8px 0 0;
  font-size: 14px;
  opacity: 0.92;
  line-height: 1.45;
}

.invite-card-body {
  padding: 24px 28px 28px;
  text-align: center;
}

.invite-card-header .error-icon {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  color: #fff;
}

.invite-card-header .error-icon svg {
  width: 48px;
  height: 48px;
}

.invite-card-header .error-title {
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
}

.invite-card-header .error-description {
  position: relative;
  z-index: 1;
  margin: 10px 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.5;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top: 4px solid var(--accent-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.workspace-spotlight {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px;
  margin-bottom: 20px;
  text-align: left;
  border-radius: 4px;
  background: linear-gradient(135deg, var(--bg-primary) 0%, rgba(99, 102, 241, 0.06) 100%);
  border: 1px solid var(--border-primary);
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.08);
}

.workspace-spotlight-avatar {
  flex-shrink: 0;
  width: 104px;
  height: 104px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid var(--border-primary);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.2);
}

.workspace-spotlight-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.workspace-spotlight-initial {
  font-size: 44px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.workspace-spotlight-copy {
  flex: 1;
  min-width: 0;
}

.workspace-spotlight-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary);
  margin-bottom: 4px;
}

.workspace-spotlight-name {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.25;
  word-break: break-word;
}

.invite-perks {
  list-style: none;
  margin: 0 0 22px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.invite-perks li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--text-secondary);
  padding: 8px 12px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
}

.invite-perk-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #10b981, #059669);
}

.workspace-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

.workspace-badge.type-crm {
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.workspace-badge.type-tarefas {
  background: rgba(99, 102, 241, 0.1);
  color: #6366F1;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.badge-icon {
  display: flex;
  align-items: center;
}

.user-logged {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.auth-callout {
  padding: 14px 16px;
  border-radius: 4px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px dashed rgba(99, 102, 241, 0.35);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  border: 1px solid var(--border-primary);
}

.user-avatar {
  position: relative;
  width: 48px;
  height: 48px;
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--accent-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.user-details {
  flex: 1;
  text-align: left;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.user-email {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.user-logged,
.user-not-logged {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.action-buttons,
.auth-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.login-required {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

.error-actions {
  display: flex;
  justify-content: stretch;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.btn-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.btn-primary {
  border: none;
  color: #fff;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
}

.btn-primary--glow:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.45);
}

.btn-primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-hover);
  border-color: rgba(99, 102, 241, 0.35);
}

.btn-secondary--ghost {
  background: transparent;
  color: var(--text-secondary);
  border-color: transparent;
  box-shadow: none;
}

.btn-secondary--ghost:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Responsividade */
@media (max-width: 768px) {
  .page-content {
    padding: 24px 16px 40px;
    gap: 20px;
  }

  .invite-logo {
    height: 320px;
    max-height: min(320px, 48vh);
  }

  .invite-card-header {
    padding: 28px 20px 20px;
  }

  .invite-title {
    font-size: 22px;
  }

  .invite-card-body {
    padding: 20px 18px 24px;
  }

  .workspace-spotlight {
    flex-direction: column;
    text-align: center;
  }

  .workspace-spotlight-copy {
    text-align: center;
  }

  .user-info {
    flex-direction: column;
    text-align: center;
  }

  .user-details {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .invite-logo {
    height: 240px;
    max-height: min(240px, 40vh);
  }
  
  .invite-brand {
    font-size: 24px;
  }
}

.accept-invite-hide-header {
  display: none !important;
}
</style>
